import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import FavoriteIcon from "@mui/icons-material/Favorite";
import TelegramIcon from "@mui/icons-material/Telegram";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DownloadIcon from "@mui/icons-material/Download";
import {
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Typography,
  CardMedia,
  CardContent,
  TextField,
  Badge,
  Popover,
  ClickAwayListener,
  InputAdornment,
  CircularProgress,
  Divider,
  Card,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  fetchPhotos,
  getCurrentDateYYYYMM,
  fetchDelete,
  fetchUpdate,
  fetchTodayPhotoLength,
  fetchLike,
  fetchSaveCommentToBackend,
} from "../Hooks/Api";
import MySnackbar from "../Hooks/MySnackBar";
import UploadButton from "./UploadButton";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { TokenAtom, TokenInfo, NewPhotoStates } from "../Hooks/TokenAtom";
import { downloadFile } from "../downloadFile";
import "dayjs/locale/ko";
import dayjs from "dayjs";
import { createTheme, ThemeProvider } from "@mui/material/styles";
// 날짜별로 그룹화하는 함수
const groupByDate = (items) => {
  return items.reduce((groups, item) => {
    // "YYYY-MM-DD" 형식의 날짜 문자열을 "YYYYMMDD" 형식으로 변환
    const [year, month, day] = item.date.split("-");
    const dateKey = `${year}${month}${day}`;

    if (!groups[dateKey]) {
      groups[dateKey] = {
        date: dateKey, // "YYYYMMDD" 형식으로 저장
        photos: [],
      };
    }
    groups[dateKey].photos.push(item);
    return groups;
  }, {});
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  const year = dateString.substring(0, 4);
  const month = dateString.substring(4, 6);
  const day = dateString.substring(6, 8);
  const date = new Date(`${year}-${month}-${day}`);
  const dayOfWeek = date.toLocaleDateString("ko-KR", { weekday: "long" });
  return `${year}년 ${month}월 ${day}일 (${dayOfWeek})`;
};

export const formatDateDialog = (dateString) => {
  if (!dateString) return "";

  // "YYYY-MM-DD" 형식의 날짜 문자열에서 연도, 월, 일을 추출
  const [year, month, day] = dateString.split("-");

  // Date 객체를 사용하여 요일을 추출
  const date = new Date(`${year}-${month}-${day}`);
  const options = { weekday: "long" }; // 요일을 긴 형식으로 추출
  const dayOfWeek = date.toLocaleDateString("ko-KR", options);

  // 원하는 형식으로 변환
  return `${year}년 ${month}월 ${day}일 (${dayOfWeek})`;
};

// 렌더링마다 재생성되지 않도록 컴포넌트 외부에 정의
const pickerTheme = createTheme({
  palette: {
    primary: { main: "#8c6744" },
    secondary: { main: "#cf9e62" },
  },
  typography: {
    fontFamily: "LeeSeoyun, sans-serif",
  },
});

const formatDateShort = (dateStr) => {
  return dateStr
    .replace("년 ", ".")
    .replace("월 ", ".")
    .replace("일", "")
    .replace("(일요일)", "(일)")
    .replace("(월요일)", "(월)")
    .replace("(화요일)", "(화)")
    .replace("(수요일)", "(수)")
    .replace("(목요일)", "(목)")
    .replace("(금요일)", "(금)")
    .replace("(토요일)", "(토)");
};

// 사진 카드 - React.memo로 다이얼로그 open 시 불필요한 리렌더 방지
export const getThumbnailUrl = (imageUrl) => {
  if (!imageUrl) return imageUrl;
  const relativePath = imageUrl.replace(/^\/media\//, '');
  return `/api/GetThumbnail?img=${encodeURIComponent(relativePath)}`;
};

const PhotoCard = React.memo(({ item, group, onClick }) => (
  <Card
    sx={{
      borderRadius: 2,
      boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
      cursor: "pointer",
      backgroundColor: "#f5f4f2",
    }}
    onClick={() => onClick(item, group)}
  >
    <CardMedia
      component="img"
      image={getThumbnailUrl(item.image)}
      alt={item.memo}
      loading="lazy"
      sx={{
        p: "3px",
        width: "100%",
        aspectRatio: "1 / 1",
        objectFit: "cover",
        borderRadius: 2,
      }}
    />
    <CardContent sx={{ pb: "4px !important", pt: 0, px: 1 }}>
      <Typography
        variant="body2"
        color="#8c6744"
        sx={{
          textAlign: "center",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {item.memo}
      </Typography>
    </CardContent>
  </Card>
));

const Home = () => {
  const [open, setOpen] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState([]); // 현재 날짜 그룹 사진 배열
  const [selectedIndex, setSelectedIndex] = useState(0); // 그룹 내 현재 인덱스
  const touchStartX = useRef(null); // 스와이프 감지용
  const [value, setValue] = useState(getCurrentDateYYYYMM());
  const [memo, setMemo] = useState(""); // 메모 상태 추가
  const [like_count, setLike_count] = useState(0); // 메모 상태 추가
  const [likeList, setLikeList] = useState([]); // 빈 배열로 초기화
  const [isLiked, setIsLiked] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [detailSearchOpen, setDetailSearchOpen] = useState(false);
  const [isLatest, setIsLatest] = useState(true); // 최신순/과거순 상태
  const [isSnackBarOpen, setIsSnackBarOpen] = useState(false);
  const [snackBarMessage, setSnackBarMessage] = useState("");
  const [snackBarSeverity, setSnackBarSeverity] = useState("success"); // 메시지 유형 설정
  const setTodayPhotosCount = useSetRecoilState(NewPhotoStates);
  const queryClient = useQueryClient();
  const tokens = useRecoilValue(TokenAtom);
  const tokenInfo = useRecoilValue(TokenInfo);
  const specificDate = dayjs("1990-02-03"); // 특정 날짜를 생성
  const [anchorEl, setAnchorEl] = useState(null);
  const [comments, setComments] = useState(selectedItem?.replies || []);
  const [newComment, setNewComment] = useState("");
  const [isLoadingComment, setIsLoadingComment] = useState(false);
  const inputRef = useRef();
  const [deleteAnchor, setDeleteAnchor] = useState(null);
  const openDelete = Boolean(deleteAnchor);
  const [openFull, setOpenFull] = useState(false);
  const [fullPhoto, setFullPhoto] = useState(null);
  const [slideDirection, setSlideDirection] = useState("left");
  const [animKey, setAnimKey] = useState(0);
  const imgRef = useRef();

  const {
    data: itemData,
    isLoading,
    isError,
  } = useQuery(["photos", value], () => fetchPhotos(value, tokens), {
    staleTime: 300000, // 캐시된 데이터의 유효 시간 설정 (예: 5분)
    cacheTime: 900000, // 캐시된 데이터를 유지하는 시간 설정 (예: 15분)
  });

  const mutation = useMutation((date) => fetchPhotos(date, tokens), {
    onSuccess: (data) => {
      queryClient.setQueryData(["photos", value], data); // 쿼리 데이터 업데이트
      queryClient.invalidateQueries(["photos", value]); // 쿼리 무효화
    },
  });

  // useMemo로 렌더링마다 groupByDate 재실행 방지 (early return 앞에 위치 필수)
  const groupedData = useMemo(() => groupByDate(itemData ?? []), [itemData]);

  useEffect(() => {
    if (isLiked) {
      const timer = setTimeout(() => {
        setIsLiked(false); // isLiked가 true가 된 후 0.5초 후에 다시 false로 변경
      }, 300); // 0.5초 동안 크기 변화
      return () => clearTimeout(timer); // 타이머를 정리하여 메모리 누수 방지
    }
  }, [isLiked]);

  useEffect(() => {
    if (itemData && selectedItem) {
      const updatedItem = itemData.find((item) => item.id === selectedItem.id);
      if (updatedItem) {
        setSelectedItem(updatedItem);
        setMemo(updatedItem.memo);
        setLike_count(updatedItem.like_count);
        setLikeList([updatedItem.likeList]);
        // 그룹 내 사진도 최신 데이터로 갱신
        if (selectedGroup.length > 0) {
          setSelectedGroup((prev) =>
            prev.map((p) => {
              const fresh = itemData.find((d) => d.id === p.id);
              return fresh ?? p;
            })
          );
        }
      }
    }
  // selectedItem.id만 의존: 객체 전체를 의존하면 setSelectedItem 호출 시 무한루프 발생
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem?.id, itemData]);

  const handleEditClickOpen = () => {
    setEditOpen(true);
  };

  const handleEditClose = () => {
    setEditOpen(false);
  };
  const handleDetailClickOpen = () => {
    setDetailSearchOpen(true);
  };

  const handleDetailClose = () => {
    setDetailSearchOpen(false);
  };

  const handleResetClick = () => {
    const newDate = getCurrentDateYYYYMM();
    setValue(newDate);
    mutation.mutate(newDate);
  };

  const handleNewClick = () => {
    setValue(specificDate);
    mutation.mutate(specificDate);
  };

  const handleClickOpen = useCallback((item, group) => {
    const idx = group.findIndex((p) => p.id === item.id);
    setSelectedGroup(group);
    setSelectedIndex(idx);
    setSelectedItem(item);
    setComments(item?.replies);
    setOpen(true);
  }, []);

  const handlePrev = useCallback(() => {
    setSlideDirection("right");
    setAnimKey((k) => k + 1);
    setSelectedIndex((prev) => {
      const next = prev - 1;
      const item = selectedGroup[next];
      setSelectedItem(item);
      setComments(item?.replies || []);
      return next;
    });
  }, [selectedGroup]);

  const handleNext = useCallback(() => {
    setSlideDirection("left");
    setAnimKey((k) => k + 1);
    setSelectedIndex((prev) => {
      const next = prev + 1;
      const item = selectedGroup[next];
      setSelectedItem(item);
      setComments(item?.replies || []);
      return next;
    });
  }, [selectedGroup]);

  // 인접 사진 미리 로드 → 네비게이션 시 캐시에서 즉시 표시
  useEffect(() => {
    [selectedIndex - 1, selectedIndex + 1].forEach((i) => {
      if (i >= 0 && i < selectedGroup.length && selectedGroup[i]?.image) {
        const img = new window.Image();
        img.src = `${selectedGroup[i].image}?w=248&fit=crop&auto=format`;
      }
    });
  }, [selectedIndex, selectedGroup]);

  // key 리마운트 없이 CSS 애니메이션 재시작 (reflow 트릭)
  useEffect(() => {
    const el = imgRef.current;
    if (!el || animKey === 0) return;
    el.style.animation = "none";
    el.getBoundingClientRect(); // force reflow
    el.style.animation = `${
      slideDirection === "left" ? "slideInFromLeft" : "slideInFromRight"
    } 0.25s ease-out`;
  }, [animKey, slideDirection]);

  const handleClose = () => {
    setOpen(false);
    setSelectedItem(null);
    setSelectedGroup([]);
    setSelectedIndex(0);
    setComments([]);
  };

  const handleDeleteClick = (id) => {
    setSelectedItem({ ...selectedItem, id });
    setOpenConfirmDialog(true);
  };
  const handleDetailSearch = () => {
    const keyword = inputRef.current.value;
    setValue(keyword);
    mutation.mutate(keyword);
    handleDetailClose();
  };
  const handleClick = (event) => {
    setDeleteAnchor(event.currentTarget);
  };

  const handleDeleteClose = () => {
    setDeleteAnchor(null);
  };

  const handleDeleteAnchorClick = () => {
    handleDeleteClick(selectedItem?.id); // 실제 삭제 함수 호출
    handleDeleteClose(); // 메뉴 닫기
  };

  const handleAddComment = async () => {
    const newCommentObj = {
      id: selectedItem?.id,
      diffId: comments.length + 1,
      writer: tokenInfo?.first_name,
      content: newComment,
      diff: "photo",
    };
    try {
      setIsLoadingComment(true);
      const status = await fetchSaveCommentToBackend(newCommentObj, tokens);

      if (status === 201) {
        queryClient.invalidateQueries(["photos", value]);
        setComments([...comments, newCommentObj]);
        setNewComment("");
        setSnackBarMessage("댓글작성 성공");
        setSnackBarSeverity("success");
        setIsSnackBarOpen(true);
      } else {
        if (status === 401) {
          setSnackBarMessage("권한없음");
          setSnackBarSeverity("error");
          setIsSnackBarOpen(true);
        } else {
          setSnackBarMessage("서버오류");
          setSnackBarSeverity("error");
          setIsSnackBarOpen(true);
        }
        console.error("Failed to delete photo, status code:", status);
      }
    } catch (error) {
      setSnackBarMessage("통신오류");
      setSnackBarSeverity("error");
      setIsSnackBarOpen(true);
      console.error("Error handling delete click:", error);
    } finally {
      setOpenConfirmDialog(false);
      setIsLoadingComment(false);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      const status = await fetchDelete(selectedItem?.id, tokens);
      if (status === 204) {
        queryClient.invalidateQueries(["photos", value]);

        fetchTodayPhotoLength(tokens, setTodayPhotosCount);
        setSnackBarMessage("삭제 성공");
        setSnackBarSeverity("success");
        setIsSnackBarOpen(true);
        handleClose();
      } else {
        if (status === 401) {
          setSnackBarMessage("권한없음");
          setSnackBarSeverity("error");
          setIsSnackBarOpen(true);
        } else if (status === 404) {
          setSnackBarMessage("사진없음");
          setSnackBarSeverity("error");
          setIsSnackBarOpen(true);
        } else {
          setSnackBarMessage("서버오류");
          setSnackBarSeverity("error");
          setIsSnackBarOpen(true);
        }
        console.error("Failed to delete photo, status code:", status);
      }
    } catch (error) {
      setSnackBarMessage("통신오류");
      setSnackBarSeverity("error");
      setIsSnackBarOpen(true);
      console.error("Error handling delete click:", error);
    } finally {
      setOpenConfirmDialog(false);
    }
  };

  const handleUpdate = async () => {
    try {
      const status = await fetchUpdate(selectedItem?.id, memo, tokens);
      if (status === 200) {
        selectedItem.memo = memo;
        queryClient.invalidateQueries(["photos", value]);
        setSnackBarMessage("업데이트 성공");
        setSnackBarSeverity("success");
        setIsSnackBarOpen(true);
        handleEditClose();
      } else {
        if (status === 401) {
          setSnackBarMessage("권한없음");
          setSnackBarSeverity("error");
          setIsSnackBarOpen(true);
        } else if (status === 404) {
          setSnackBarMessage("사진없음");
          setSnackBarSeverity("error");
          setIsSnackBarOpen(true);
        } else {
          setSnackBarMessage("서버오류");
          setSnackBarSeverity("error");
          setIsSnackBarOpen(true);
        }
        console.error("Failed to delete photo, status code:", status);
      }
    } catch (error) {
      setSnackBarMessage("통신오류");
      setSnackBarSeverity("error");
      setIsSnackBarOpen(true);
      console.error("Error handling delete click:", error);
    }
  };

  const handleUploadSuccess = (status) => {
    if (status === 201) {
      setSnackBarMessage("업로드 성공");
      setSnackBarSeverity("success");
      setIsSnackBarOpen(true);
      queryClient.invalidateQueries(["photos", value]); // 쿼리 무효화
    } else if (status === 401) {
      setSnackBarMessage("권한없음");
      setSnackBarSeverity("error");
      setIsSnackBarOpen(true);
    } else if (status === 404) {
      setSnackBarMessage("날짜없음");
      setSnackBarSeverity("error");
      setIsSnackBarOpen(true);
    } else {
      setSnackBarMessage("서버오류");
      setSnackBarSeverity("error");
      setIsSnackBarOpen(true);
    }
  };

  const handleToggleClick = () => {
    setIsLatest((prevIsLatest) => !prevIsLatest); // 상태 토글
  };

  const handleCancelDelete = () => {
    setOpenConfirmDialog(false);
  };
  const handleCloseSnackBar = () => {
    setIsSnackBarOpen(false);
  };
  const handleFavoriteClick = async (id, writer) => {
    try {
      const status = await fetchLike(id, writer, tokens, 0);
      if (status === 200) {
        setIsLiked((prev) => !prev);
        queryClient.invalidateQueries(["photos", value]);
        setSnackBarMessage("좋아요!!");
        setSnackBarSeverity("success");
        setIsSnackBarOpen(true);
      } else {
        if (status === 401) {
          setSnackBarMessage("권한없음");
          setSnackBarSeverity("error");
          setIsSnackBarOpen(true);
        } else if (status === 404) {
          setSnackBarMessage("사진없음");
          setSnackBarSeverity("error");
          setIsSnackBarOpen(true);
        } else {
          setSnackBarMessage("서버오류");
          setSnackBarSeverity("error");
          setIsSnackBarOpen(true);
        }
        console.error("Failed to delete photo, status code:", status);
      }
    } catch (error) {
      if (error.status === 406) {
        setSnackBarMessage("이미 눌렀습니다");
        setSnackBarSeverity("warning");
        setIsSnackBarOpen(true);
      } else {
        setSnackBarMessage("통신오류");
        setSnackBarSeverity("error");
        setIsSnackBarOpen(true);
      }
      console.error("Error handling delete click:", error);
    }
  };

  // Popover 열기
  const handleBadgeClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // Popover 닫기
  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  // Popover가 열려있는지 확인
  const openPopover = Boolean(anchorEl);
  const popOpen = openPopover ? "simple-popover" : undefined;

  const handleOpenFull = (photo) => {
    setFullPhoto(photo);
    setOpenFull(true);
  };
  const handleCloseFull = () => setOpenFull(false);

  if (isLoading) {
    return <div>로딩중...</div>;
  }

  if (isError) {
    return <div>로그인을 다시 해주세요</div>;
  }

  return (
    <Box sx={{ width: "100%", height: "auto", pb: 3 }}>
      <Box
        sx={{
          width: "100%",
          height: "auto",
          mb: 1,
          mt: 0,
          mx: 0,
          px: 2,
          border: "1px solid #ddd",
          borderRadius: "8px",
          backgroundColor: "#f5f1e7",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)", // 그림자 추가
        }}
      >
        <Box sx={{ width: "100%", height: "auto", mb: 1, mt: 0, pb: 1 }}>
          <Grid container spacing={1} justifyContent="center" sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <ThemeProvider theme={pickerTheme}>
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale="ko"
                >
                  <DesktopDatePicker
                    sx={{ mt: 1, width: "100%" }}
                    margin="dense"
                    format="YYYY년 MM월"
                    label="캘린더"
                    value={
                      dayjs(value).isSame(specificDate, "day")
                        ? dayjs()
                        : dayjs(value)
                    }
                    onChange={(newValue) => setValue(newValue)}
                    views={["year", "month"]}
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: {
                          width: "100%",
                          height: "40px",
                          backgroundColor: "white", // ← 폼 배경 하얗게
                          borderRadius: "8px", // ← 둥근 모서리(선택)
                          boxShadow: "0 2px 8px rgba(0,0,0,0.15)", // ← 그림자
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#8c6744",
                          },

                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#8c6744",
                          },

                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#8c6744",
                          },
                          "& .MuiInputBase-input": {
                            backgroundColor: "white",
                            color: "#8c6744",
                          },
                          "& .MuiFormLabel-root": {
                            color: "#8c6744",
                          },
                          "& .MuiFormLabel-root.Mui-focused": {
                            color: "#8c6744", // 예: 강조색
                          },
                          "& .MuiSvgIcon-root path": {
                            fill: "#8c6744",
                          },
                        },
                      },
                      actionBar: {
                        actions: ["today"],
                      },
                    }}
                  />
                </LocalizationProvider>
              </ThemeProvider>
            </Grid>
          </Grid>
          <Grid
            container
            spacing={1}
            justifyContent="center"
            sx={{ pt: "4px" }}
          >
            <Grid item xs={4} sx={{ display: "flex", alignItems: "center" }}>
              <Button
                variant="contained"
                onClick={handleDetailClickOpen}
                sx={{
                  width: "100%",
                  height: "40px",
                  background: "linear-gradient(45deg, #b78609, #edb56f)",
                  color: "white",
                  "&:hover": {
                    background: "linear-gradient(45deg, #a67855, #f8e5d0)",
                  },
                }} // 캘린더와 동일한 높이 설정
              >
                상세검색
              </Button>
            </Grid>
            <Grid item xs={3}>
              <Button
                variant="contained"
                onClick={handleNewClick}
                sx={{
                  width: "100%",
                  height: "40px", // 캘린더와 동일한 높이
                  background: "linear-gradient(45deg, #8e44ad, #e84393)",
                  color: "#fff",
                  borderRadius: 1,
                  "&:hover": {
                    background: "linear-gradient(45deg, #a64fcf, #ff5ea2)",
                  },
                }}
              >
                NEW
              </Button>
            </Grid>
            <Grid item xs={2}>
              <IconButton
                onClick={handleResetClick}
                sx={{
                  width: 40,
                  height: 40,
                  background: "linear-gradient(45deg, #b78609, #edb56f)",
                  color: "white",
                  borderRadius: "50%", // 동그랗게
                  "&:hover": {
                    background: "linear-gradient(45deg, #a67855, #f8e5d0)",
                  },
                }}
              >
                <RestartAltIcon />
              </IconButton>
            </Grid>

            <Grid item xs={3}>
              <Button
                variant="contained"
                onClick={handleToggleClick}
                sx={{
                  width: "100%",
                  height: "40px",
                  color: "white",
                  background: isLatest
                    ? "linear-gradient(45deg, #b78609, #edb56f)" // 과거순 배경
                    : "linear-gradient(45deg, #8e44ad, #e84393)", // 최신순 배경
                  "&:hover": {
                    background: isLatest
                      ? "linear-gradient(45deg, #b78609, #edb56f)" // 과거순 호버
                      : "linear-gradient(45deg, #8e44ad, #e84393)", // 최신순 호버
                  },
                }}
              >
                {isLatest ? "과거순" : "최신순"}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>
      {(isLatest
        ? Object.keys(groupedData).sort().reverse()
        : Object.keys(groupedData).sort()
      ).map((dateKey) => (
        <Card
          key={dateKey}
          sx={{
            width: "100vw", // 화면 전체 폭
            ml: -1,
            mb: 1,
            px: 2,
            py: 1,
            // borderRadius: 2,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            backgroundColor: "#f5f4f2",
          }}
        >
          {/* 날짜 제목 */}
          <Typography variant="subtitle1" sx={{ mb: 1, color: "#8c6744" }}>
            {formatDate(groupedData[dateKey].date)}
          </Typography>

          {/* 사진 리스트 */}
          <Box
            sx={{
              width: "100%",
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 1, // 사진 간 간격
            }}
          >
            {groupedData[dateKey].photos.map((item) => (
              <PhotoCard
                key={item.id}
                item={item}
                group={groupedData[dateKey].photos}
                onClick={handleClickOpen}
              />
            ))}
          </Box>
        </Card>
      ))}

      {tokenInfo.is_superuser && (
        <UploadButton onUploadSuccess={handleUploadSuccess} />
      )}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        transitionDuration={150}
        PaperProps={{
          sx: {
            bgcolor: "#f5f4f2",
            borderRadius: "12px",
          },
        }}
      >
        <DialogTitle sx={{ px: 2, pt: 1, pb: 0 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              bgcolor: "#f5f4f2",
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontSize: "1.2rem",
                color: "#8c6744",
                whiteSpace: "nowrap", // 한 줄로 표시
                overflow: "hidden", // 넘치는 부분 숨기기
                textOverflow: "ellipsis", // ...으로 표시
              }}
            >
              {/* {formatDateDialog(selectedItem?.date)} */}
              {selectedItem?.memo}
            </Typography>
            {tokenInfo.is_superuser && (
              <>
                <IconButton onClick={handleClick} sx={{ px: 0 }}>
                  <MoreHorizIcon />
                </IconButton>
                <Menu
                  anchorEl={deleteAnchor}
                  open={openDelete}
                  onClose={handleDeleteClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  PaperProps={{
                    sx: {
                      py: 0, // 메뉴 상하 여백 제거
                    },
                  }}
                  MenuListProps={{
                    sx: {
                      pb: 0, // ul padding 제거
                      margin: 0, // ul margin 제거
                    },
                  }}
                >
                  <MenuItem
                    onClick={handleEditClickOpen}
                    sx={{
                      minHeight: 0, // 메뉴 항목 최소 높이 제거
                      color: "#B8860B",
                      py: 0, // 상하 padding 줄임
                      px: 2, // 좌우 padding
                    }}
                  >
                    수정
                  </MenuItem>
                  <Divider
                    sx={{ mt: 0 + " !important", mb: " 4px !important" }}
                  />{" "}
                  <MenuItem
                    onClick={handleDeleteAnchorClick}
                    sx={{
                      minHeight: 0, // 메뉴 항목 최소 높이 제거
                      color: "red",
                      py: 0, // 상하 padding 줄임
                      px: 2, // 좌우 padding
                    }}
                  >
                    삭제
                  </MenuItem>
                  <Divider
                    sx={{ mt: 0 + " !important", mb: " 4px !important" }}
                  />{" "}
                  {/* 메뉴 아이템 구분선, 여백 최소화 */}
                  <MenuItem
                    onClick={handleDeleteClose}
                    sx={{
                      minHeight: 0, // 메뉴 항목 최소 높이 제거
                      py: 0,
                      px: 2,
                    }}
                  >
                    취소
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: 0,
            bgcolor: "#f5f4f2",
          }}
        >
          {selectedItem?.image && (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                px: 2,
                pt: 1,
                pb: 0,
              }}
              onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
              onTouchEnd={(e) => {
                if (touchStartX.current === null) return;
                const dx = e.changedTouches[0].clientX - touchStartX.current;
                touchStartX.current = null;
                if (dx < -50 && selectedIndex < selectedGroup.length - 1) handleNext();
                else if (dx > 50 && selectedIndex > 0) handlePrev();
              }}
            >
              {/* 이전 버튼 */}
              {selectedIndex > 0 && (
                <IconButton
                  onClick={handlePrev}
                  sx={{
                    position: "absolute",
                    left: 4,
                    zIndex: 1,
                    bgcolor: "rgba(255,255,255,0.75)",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.95)" },
                    p: "4px",
                  }}
                >
                  <ChevronLeftIcon sx={{ color: "#8c6744" }} />
                </IconButton>
              )}

              <style>{`
                @keyframes slideInFromLeft {
                  from { transform: translateX(40px); }
                  to   { transform: translateX(0); }
                }
                @keyframes slideInFromRight {
                  from { transform: translateX(-40px); }
                  to   { transform: translateX(0); }
                }
              `}</style>
              <img
                ref={imgRef}
                src={`${selectedItem.image}?w=248&fit=crop&auto=format`}
                alt={selectedItem.memo}
                loading="eager"
                style={{
                  backgroundColor: "#f5f4f2",
                  width: "100%",
                  height: "auto",
                  borderRadius: "8px",
                  border: "1px solid rgba(0, 0, 0, 0.1)",
                  boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
                  marginBottom: "8px",
                  padding: "4px",
                }}
                onClick={() => handleOpenFull(selectedItem.image)}
              />

              {/* 다음 버튼 */}
              {selectedIndex < selectedGroup.length - 1 && (
                <IconButton
                  onClick={handleNext}
                  sx={{
                    position: "absolute",
                    right: 4,
                    zIndex: 1,
                    bgcolor: "rgba(255,255,255,0.75)",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.95)" },
                    p: "4px",
                  }}
                >
                  <ChevronRightIcon sx={{ color: "#8c6744" }} />
                </IconButton>
              )}
            </Box>
          )}
          {/* 페이지 인디케이터 */}
          {selectedGroup.length > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", gap: "6px", pt: "4px", pb: 0 }}>
              {selectedGroup.map((_, i) => (
                <Box
                  key={i}
                  onClick={() => {
                    setSelectedIndex(i);
                    setSelectedItem(selectedGroup[i]);
                    setComments(selectedGroup[i]?.replies || []);
                  }}
                  sx={{
                    width: i === selectedIndex ? 16 : 8,
                    height: 8,
                    borderRadius: 4,
                    bgcolor: i === selectedIndex ? "#8c6744" : "#c9b08a",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                />
              ))}
            </Box>
          )}
          <Box
            sx={{
              pb: 0,
              px: 2,
              width: "100%",
              backgroundColor: "#f5f4f2",
              borderRadius: "0 0 8px 8px",
            }}
          >
            <Grid
              container
              alignItems="stretch" // 각 항목의 높이를 동일하게 설정
              justifyContent="space-between"
              sx={{ height: "100%" }} // 부모 Grid의 높이를 설정
            >
              <Grid item>
                <Badge
                  badgeContent={like_count}
                  overlap="circular"
                  onClick={handleBadgeClick}
                  sx={{
                    "& .MuiBadge-badge": {
                      backgroundColor: "#B8860B", // ★ 원하는 HEX 색상 적용
                      color: "white", // 텍스트 색상
                      fontSize: "0.8rem", // 뱃지 내 텍스트의 크기 조절
                      height: "18px", // 뱃지의 높이 조절
                      minWidth: "18px", // 뱃지의 최소 너비 조절
                      padding: "0", // 패딩 제거로 크기 축소
                      transform: "scale(0.8) translate(30%, -30%)", // 크기 및 위치 조정
                    },
                  }}
                >
                  <IconButton
                    onClick={(event) => {
                      event.stopPropagation(); // 이벤트 전파 방지
                      handleFavoriteClick(
                        selectedItem?.id,
                        tokenInfo?.first_name
                      );
                    }}
                    sx={{ pl: 0 }}
                  >
                    {isLiked !== undefined && ( // 조건부 렌더링
                      // <Grow in={isLiked} timeout={500}>
                      <FavoriteIcon
                        sx={{
                          color: "error.main", // 항상 빨간색으로 유지
                          transform: isLiked ? "scale(1.5)" : "scale(1)", // isLiked가 true일 때만 크기를 1.5배로 증가
                          transition: "transform 0.2s ease-in-out", // 0.3초 동안 크기 변환 애니메이션
                        }}
                      />
                      // </Grow>
                    )}
                    {/* <FavoriteIcon color="error" /> */}
                  </IconButton>
                  {/* Popover: 배지 클릭 시 나타나는 텍스트 */}
                  <Popover
                    id={popOpen}
                    open={openPopover}
                    anchorEl={anchorEl}
                    onClose={handleClosePopover}
                    anchorOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                  >
                    <ClickAwayListener onClickAway={handleClosePopover}>
                      <Typography
                        sx={{
                          p: 1,
                          fontSize: "0.8rem",
                          whiteSpace: "pre-line", // 줄바꿈을 적용하기 위한 속성
                        }}
                      >
                        {likeList && likeList.length > 0
                          ? likeList.join("\n") // 배열 요소들을 한 줄씩 연결
                          : "No likes yet"}
                      </Typography>
                    </ClickAwayListener>
                  </Popover>
                </Badge>
              </Grid>
              <Grid item>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 2,
                    color: "text.secondary",
                    textAlign: "right",
                    height: "100%",
                    display: "flex",
                    alignItems: "center", // 수직 중앙 정렬
                    justifyContent: "flex-end", // 수평 정렬을 오른쪽으로 유지
                  }}
                >
                  {formatDateShort(formatDateDialog(selectedItem?.date))}
                </Typography>
              </Grid>
              <Grid item>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 2,
                    color: "text.secondary",
                    textAlign: "right",
                    height: "100%",
                    display: "flex",
                    alignItems: "center", // 수직 중앙 정렬
                    justifyContent: "flex-end", // 수평 정렬을 오른쪽으로 유지
                  }}
                >
                  작성자: {selectedItem?.writer}
                </Typography>
              </Grid>
            </Grid>
          </Box>
          <Box
            sx={{
              width: "100%",
              px: 2,
              pb: 1,
              pt: 0,
              bgcolor: " #f5f4f2",
              borderRadius: "8px",
              mt: 0,
            }}
          >
            <Typography sx={{ fontSize: "1rem" }} gutterBottom>
              댓글
            </Typography>

            {/* Display existing comments */}
            {comments && comments.length > 0 ? (
              comments.map((comment, index) => (
                <Box key={index} sx={{ mb: 1, ml: 1 }}>
                  <Typography variant="body2" color="textSecondary">
                    {comment.writer}: {comment.content}
                  </Typography>
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="textSecondary"></Typography>
            )}
            <Grid container spacing={1} alignItems="center">
              <Grid item xs>
                <Box sx={{ borderRadius: 2 }}>
                  <TextField
                    label="댓글을 작성해 주세요"
                    fullWidth
                    variant="outlined"
                    value={newComment}
                    size="small"
                    onChange={(e) => setNewComment(e.target.value)}
                    InputLabelProps={{
                      sx: {
                        fontSize: "0.85rem",
                        color: "#8c6744", // 기본 라벨 색
                        "&.Mui-focused": {
                          color: "#8c6744", // 📌 라벨이 위로 올라갔을 때(포커스) 색상
                        },
                        "&.MuiFormLabel-filled": {
                          color: "#8c6744", // 📌 텍스트 입력되어 라벨이 위로 간 상태
                        },
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleAddComment}
                            disabled={isLoadingComment}
                          >
                            {isLoadingComment ? (
                              <CircularProgress size={24} />
                            ) : (
                              <TelegramIcon />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                      sx: {
                        backgroundColor: "white",
                        borderRadius: "8px",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#8c6744",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#8c6744",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#8c6744",
                        },
                        "& .MuiInputBase-input": {
                          borderRadius: "8px",
                          backgroundColor: "white",
                          color: "#8c6744",
                        },
                        "& .MuiSvgIcon-root path": {
                          fill: "#8c6744",
                        },
                      },
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            bgcolor: "#f5f4f2",
            display: "flex",
            px: 2,
          }}
        >
          <Button
            onClick={() => {
              downloadFile(selectedItem.image, selectedItem.image);
            }}
            variant="contained"
            startIcon={<DownloadIcon />}
            sx={{
              background: "#B8860B",
              color: "white",
              fontWeight: "bold",
              "&:hover": {
                background: "#DAA520",
              },
            }}
          >
            다운로드
          </Button>

          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              color: "#B8860B",
              borderColor: "#B8860B", // ← outlined 라서 border도 맞춰줘야 함
              "&:hover": {
                borderColor: "#DAA520",
                background: "#DAA520",
              },
            }}
          >
            닫기
          </Button>
        </DialogActions>
      </Dialog>
      {/*상세검색 다이어로그 */}
      <Dialog
        open={detailSearchOpen}
        onClose={handleDetailClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "#f5f4f2", // 전체 배경 색
            borderRadius: "12px", // 모서리 둥글게
          },
        }}
      >
        <DialogTitle sx={{ color: "#8c6744", bgcolor: "#f5f4f2" }}>
          단어 검색
        </DialogTitle>
        <DialogContent
          sx={{
            bgcolor: "#f5f4f2",
            pt: "16px !important",
          }}
        >
          <TextField
            label="검색어 입력"
            variant="outlined"
            fullWidth
            inputRef={inputRef}
            margin="dense"
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#f5f4f2",
                "& fieldset": {
                  borderColor: "#c9b08a",
                },
                "&:hover fieldset": {
                  borderColor: "#b89263",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#8c6744",
                  borderWidth: "2px",
                },
              },
              "& .MuiInputLabel-root": {
                color: "#8c6744",
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "#8c6744",
              },
              /* ✨ 입력 텍스트 색상 변경 */
              "& .MuiInputBase-input": {
                color: "#8c6744", // ← 원하는 텍스트 색상
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDetailSearch}
            variant="contained"
            sx={{
              background: "#B8860B", // 민트 → 초록
              color: "white",
              fontWeight: "bold",
              "&:hover": {
                background: "#DAA520", // hover 버전
              },
            }}
          >
            검색
          </Button>
          <Button
            onClick={handleDetailClose}
            variant="outlined"
            sx={{
              color: "#B8860B",
              borderColor: "#B8860B", // ← outlined 라서 border도 맞춰줘야 함
              "&:hover": {
                borderColor: "#DAA520",
                background: "#DAA520",
              },
            }}
          >
            취소
          </Button>
        </DialogActions>
      </Dialog>
      {/*수정 다이어로그 */}
      <Dialog open={editOpen} onClose={handleEditClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: "#8c6744", bgcolor: "#f5f4f2" }}>
          메모 수정
        </DialogTitle>
        <DialogContent sx={{ bgcolor: "#f5f4f2" }}>
          <TextField
            label="메모"
            fullWidth
            multiline
            rows={2}
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            sx={{
              mt: 2,
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#f5f4f2",
                "& fieldset": {
                  borderColor: "#c9b08a",
                },
                "&:hover fieldset": {
                  borderColor: "#b89263",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#8c6744",
                  borderWidth: "2px",
                },
              },
              "& .MuiInputLabel-root": {
                color: "#8c6744",
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "#8c6744",
              },
              /* ✨ 입력 텍스트 색상 변경 */
              "& .MuiInputBase-input": {
                color: "#8c6744", // ← 원하는 텍스트 색상
              },
            }}
            inputProps={{
              style: { fontSize: "14px" }, // 입력 필드의 글자 크기
            }}
          />
        </DialogContent>
        <DialogActions sx={{ bgcolor: "#f5f4f2" }}>
          <Button
            onClick={handleUpdate}
            variant="contained"
            sx={{
              background: "#B8860B", // 민트 → 초록
              color: "white",
              fontWeight: "bold",
              "&:hover": {
                background: "#DAA520", // hover 버전
              },
            }}
          >
            저장
          </Button>
          <Button
            onClick={handleEditClose}
            variant="outlined"
            sx={{
              color: "#B8860B",
              borderColor: "#B8860B", // ← outlined 라서 border도 맞춰줘야 함
              "&:hover": {
                borderColor: "#DAA520",
                background: "#DAA520",
              },
            }}
          >
            취소
          </Button>
        </DialogActions>
      </Dialog>
      {/* 삭제 확인 다이얼로그 */}
      <Dialog
        open={openConfirmDialog}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>삭제 확인</DialogTitle>
        <DialogContent>
          <DialogContentText>
            정말로 이 사진을 삭제하시겠습니까?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            취소
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="secondary"
            variant="contained"
          >
            삭제
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openFull}
        onClose={handleCloseFull}
        fullScreen
        PaperProps={{
          sx: {
            backgroundColor: "rgba(0,0,0,0.9)",
          },
        }}
      >
        {/* 닫기 버튼 */}

        <IconButton
          onClick={handleCloseFull}
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            color: "white",
            zIndex: 10,
          }}
        >
          <CloseIcon fontSize="large" />
        </IconButton>

        {/* 전체 사진 영역 */}
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <img
            src={fullPhoto}
            alt=""
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
            }}
          />
        </Box>
      </Dialog>

      <MySnackbar
        open={isSnackBarOpen}
        onClose={handleCloseSnackBar}
        message={snackBarMessage}
        severity={snackBarSeverity}
      />
    </Box>
  );
};

export default Home;
